import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";

export const Route = createFileRoute("/_authenticated/reports/insurance-claims")({
  component: InsuranceClaimsReport,
});

function InsuranceClaimsReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "insurance-claims", currentBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (currentBranchId) p.set("branch", currentBranchId);
      const res = await apiGet(`/reports/insurance-claims/?${p.toString()}`);
      return res as any[];
    }
  });

  const exportRows = useMemo(() => () => (data ?? []).map((r) => ({
    "Claim No": r.claim_number,
    "Policy": r.policy_number,
    "Asset": r.asset_code,
    "Date": r.claim_date,
    "Claimed": r.claim_amount,
    "Settled": r.settled_amount,
    "Status": r.status,
  })), [data]);

  return (
    <ReportShell
      id="R-33"
      title="Insurance Claims Register"
      description="Claims raised, status, settled amount and recovery."
      filters={filters}
      onFiltersChange={setFilters}
      onExport={exportRows}
      exportFilename="Insurance_Claims"
      allowedFilters={["branchId"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim No</TableHead>
                  <TableHead>Policy</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Claimed</TableHead>
                  <TableHead className="text-right">Settled</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No data found</TableCell></TableRow>
                )}
                {data?.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.claim_number}</TableCell>
                    <TableCell>{r.policy_number}</TableCell>
                    <TableCell>{r.asset_code}</TableCell>
                    <TableCell>{r.claim_date}</TableCell>
                    <TableCell className="text-right">{r.claim_amount}</TableCell>
                    <TableCell className="text-right">{r.settled_amount}</TableCell>
                    <TableCell><Badge variant="outline">{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
